import { Menu } from 'antd';
import { FileTextOutlined, ApiOutlined, CodeOutlined, DatabaseOutlined, ToolOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

interface DocFile {
  path: string;
  title: string;
  category: string;
}

interface NavigationProps {
  docs: DocFile[];
  currentPath: string;
  onNavigate: (path: string) => void;
  title?: string;
}

type MenuItem = Required<MenuProps>['items'][number];

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'main':
      return <FileTextOutlined />;
    case 'pages':
      return <FileTextOutlined />;
    case 'endpoints':
      return <ApiOutlined />;
    case 'examples':
      return <CodeOutlined />;
    case 'schemas':
      return <DatabaseOutlined />;
    case 'admin':
      return <ToolOutlined />;
    default:
      return <FileTextOutlined />;
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'main':
      return 'Documentation';
    case 'pages':
      return 'Portal Pages';
    case 'endpoints':
      return 'API Endpoints';
    case 'examples':
      return 'Code Examples';
    case 'schemas':
      return 'Data Schemas';
    case 'admin':
      return 'Setup Guides';
    default:
      return category;
  }
};

export const Navigation = ({ docs, currentPath, onNavigate, title = 'API Documentation' }: NavigationProps) => {
  // Group docs by category
  const groupedDocs = docs.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, DocFile[]>);

  // Build menu items
  const menuItems: MenuItem[] = Object.entries(groupedDocs).map(([category, categoryDocs]) => ({
    type: 'group',
    label: getCategoryLabel(category),
    children: categoryDocs.map((doc) => ({
      key: doc.path,
      icon: getCategoryIcon(doc.category),
      label: doc.title,
    })),
  }));

  const selectedKeys = [currentPath];

  const handleMenuClick = ({ key }: { key: string }) => {
    onNavigate(key);
  };

  return (
    <div className="api-docs-navigation">
      <div className="api-docs-navigation-header">
        <h2>{title}</h2>
      </div>
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </div>
  );
};

